import React from "react";
import { canRunAnalysis } from "./helpers";

/**
 * Step two: accept the AI's peer group or narrow it.
 *
 * @param {object} props - Peers, selection, tab state and handlers.
 * @returns {JSX.Element} The selection card.
 *
 * @remarks
 * Both tabs read from one selection. The AI Recommended tab renders every peer
 * checked and disabled rather than holding a second copy of the list, so
 * switching tabs cannot silently change what will be analysed.
 */
const CompetitorSelector = ({
  competitors,
  selectedIds,
  mode,
  onModeChange,
  onToggle,
  onRunAnalysis,
  onWhy,
  busy,
}) => {
  const isRecommended = mode === "ai";
  const shown = isRecommended ? competitors.map((peer) => peer.id) : selectedIds;

  return (
    <section className="aica-card">
      <div className="aica-card-head">
        <div>
          <h2>Step 2 &middot; Select competitors</h2>
          <p className="aica-sub">
            The AI identified these peers. Switch to Customize to exclude any.
          </p>
        </div>
        <button type="button" className="aica-why" onClick={onWhy}>
          Why these competitors?
        </button>
      </div>

      <div className="aica-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={isRecommended}
          className={`aica-tab ${isRecommended ? "is-active" : ""}`}
          onClick={() => onModeChange("ai")}
        >
          ✓ AI recommended ({competitors.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!isRecommended}
          className={`aica-tab ${!isRecommended ? "is-active" : ""}`}
          onClick={() => onModeChange("custom")}
        >
          Customize
        </button>
      </div>

      <div className="aica-peer-grid">
        {competitors.map((peer) => (
          <label
            key={peer.id}
            className={`aica-peer ${isRecommended ? "is-locked" : ""}`}
          >
            <input
              type="checkbox"
              checked={shown.includes(peer.id)}
              disabled={isRecommended}
              onChange={() => onToggle(peer.id)}
              aria-label={peer.name}
            />
            <span className="aica-peer-code">{peer.shortCode}</span>
            <span className="aica-peer-name">{peer.name}</span>
          </label>
        ))}
      </div>

      <div className="aica-selection-row">
        <button
          type="button"
          className="aica-btn aica-btn-primary"
          onClick={() => onRunAnalysis(shown)}
          disabled={busy || !canRunAnalysis(shown)}
        >
          {busy ? "Starting…" : "Run competitor analysis"}
        </button>
        <span
          className={`aica-hint ${
            canRunAnalysis(shown) ? "" : "aica-hint-warn"
          }`}
        >
          {canRunAnalysis(shown)
            ? `${shown.length} of ${competitors.length} competitors selected`
            : "Select between 1 and 10 competitors to run the analysis"}
        </span>
      </div>
    </section>
  );
};

export default CompetitorSelector;
